<template>
  <ElForm
    ref="campaignInfoForm"
    :model="campaignInfoModel"
    :rules="campaignInfoRules"
    :disabled="isLoading"
    label-position="top"
    @submit.native.prevent="updateCampaignInfo"
  >
    <ElFormItem
      :label="$t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.NAME')"
      prop="name"
    >
      <ElInput v-model="campaignInfoModel.name" dir="ltr" />
    </ElFormItem>
    <ElFormItem
      prop="startDate"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.START_DATE')"
    >
      <UiDatePickerEndFuture
        :default-date="getDateValue(campaignInfoModel.startDate)"
        @select-date="setStartDate"
      />
    </ElFormItem>
    <FormItemWithHint
      prop="endDate"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.END_DATE')"
      :hint="$t('HINTS.DISCOVERY.END_DATE')"
    >
      <UiDatePickerEndFuture
        :default-date="getDateValue(campaignInfoModel.endDate)"
        @select-date="setEndDate"
      />
    </FormItemWithHint>
    <ElFormItem
      :label="$t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.STATUS')"
      prop="paused"
    >
      <AlternativeSelect
        key="value"
        v-model="campaignInfoModel.paused"
        :items="pausedOptions"
      />
    </ElFormItem>
    <FormItemWithHint
      prop="budget"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.BUDGET')"
      :hint="$t('HINTS.DISCOVERY.BUDGET')"
    >
      <ElInput v-model="campaignInfoModel.budget" name="budget" required />
    </FormItemWithHint>
    <FormItemWithHint
      prop="dailyCap"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.DAILY_CAP')"
      :hint="$t('HINTS.DISCOVERY.DAILY_CAP')"
    >
      <ElInput v-model="campaignInfoModel.dailyCap" name="dailyCap" required />
    </FormItemWithHint>
    <FormItemWithHint
      prop="targetedCpi"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.TARGETED_CPI')"
      :hint="$t('HINTS.DISCOVERY.TARGET_CPI')"
    >
      <ElInput
        v-model="campaignInfoModel.targetedCpi"
        name="targetedCpi"
        required
      />
    </FormItemWithHint>
    <ElFormItem>
      <ElCheckbox
        v-model="campaignInfoModel.hasTracker"
        @input="handleHasTrackerChange"
      >
        {{ $t('ADVERTISEMENT.CAMPAIGNS.CHOOSE_TO_HAVE_TRACKER') }}
      </ElCheckbox>
    </ElFormItem>
    <ElCollapseTransition>
      <div v-show="campaignInfoModel.hasTracker">
        <FormItemWithHint
          prop="impressionTrackerLink"
          :label="
            $t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.IMPRESSION_TRACKER_LINK')
          "
          :hint="$t('HINTS.DISCOVERY.TRACKER_EXAMPLES')"
        >
          <ElInput
            v-model="campaignInfoModel.impressionTrackerLink"
            type="url"
            dir="ltr"
          />
          <InputHelper type="warning" el-icon="el-icon-warning">
            {{ $t('ADVERTISEMENT.CAMPAIGNS.AVAILABLE_TRACKERS') }}
          </InputHelper>
        </FormItemWithHint>
      </div>
    </ElCollapseTransition>

    <ElButton
      :loading="isLoading"
      type="primary"
      native-type="submit"
      size="large"
    >
      {{ $t('SAVE') }}
    </ElButton>
    <ElButton type="text" @click.native="$emit('cancel')">
      {{ $t('CANCEL') }}
    </ElButton>
  </ElForm>
</template>

<script>
import { DiscoveryAdsCampaigns } from '@/api/api';
import { dateFormat } from '@/utils/date';
import AlternativeSelect from '@@/common/AlternativeSelect';
import UiDatePickerEndFuture from '@@/common/UiDatePickerEndFuture';
import InputHelper from '@@/common/InputHelper';
import FormItemWithHint from '@@/common/FormItemWithHint';
import form from '@/mixins/form';
import campaignId from '@/mixins/advertisement/campaignId';
import required from '@/utils/validation-rules/required';

export default {
  name: 'CampaignInfoForm',
  components: {
    UiDatePickerEndFuture,
    InputHelper,
    AlternativeSelect,
    FormItemWithHint,
  },
  mixins: [form, campaignId],
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      campaignInfoModel: {
        name: this.data.name,
        budget: this.data.budget - this.data.spentBudget,
        startDate: this.data.startDate,
        endDate: this.data.endDate,
        dailyCap: this.data.dailyCap,
        targetedCpi: this.data.targetedCpi,
        paused: this.data.paused,
        impressionTrackerLink: this.data.impressionTrackerLink,
        hasTracker: this.data.hasTracker,
      },
    };
  },
  computed: {
    pausedOptions() {
      return [
        {
          label: this.$t('ADVERTISEMENT.CAMPAIGNS.ACTIVE'),
          value: false,
        },
        {
          label: this.$t('ADVERTISEMENT.CAMPAIGNS.PAUSED'),
          value: true,
        },
      ];
    },
    campaignInfoRules() {
      const requiredRule = required(this.$t('VALIDATION_REQUIRED_ERR'));
      return {
        name: requiredRule,
        startDate: requiredRule,
        endDate: requiredRule,
        impressionTrackerLink: {
          type: 'url',
          message: this.$t('VALIDATION.URL_IS_INVALID'),
          trigger: 'change',
        },
      };
    },
  },
  methods: {
    handleHasTrackerChange(hasTracker) {
      if (!hasTracker) {
        this.resetTracker();
      }
    },
    updateCampaignInfo() {
      if (!this.isValid('campaignInfoForm')) {
        return false;
      }

      this.isLoading = true;

      const updatedBudget = Number(this.campaignInfoModel.budget);
      const totalBudget = updatedBudget + Number(this.data.spentBudget);

      return DiscoveryAdsCampaigns.updateCampaign({
        campaignId: this.campaignId,
        campaignInfo: {
          ...this.campaignInfoModel,
          budget: totalBudget,
        },
      })
        .then(message => {
          this.$message.success(message);
          this.$emit('update');
        })
        .catch(({ message }) => {
          if (message) {
            this.$message.error(message);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    setStartDate(selectedDate) {
      this.campaignInfoModel.startDate = dateFormat()('YYYY-MM-DDT00:00:00')(
        selectedDate,
      );
    },

    setEndDate(selectedDate) {
      this.campaignInfoModel.endDate = dateFormat()('YYYY-MM-DDT23:59:59')(
        selectedDate,
      );
    },

    getDateValue(date) {
      return new Date(date).valueOf();
    },

    resetTracker() {
      this.campaignInfoModel.impressionTrackerLink = '';
    },
  },
};
</script>
